import { z } from "zod";

/**
 * Per-deployment routing rules for an endpoint.
 *
 * Semantics (the Elixir engine's `Routing.Rules` implements these; the shared
 * fixtures in `tests/fixtures/routing-rules.json` pin both sides):
 * 1. A deployment is eligible when its rule evaluates to `true`; a null rule
 *    is always eligible. A rule is `false` when it divides or takes a modulo
 *    by zero, or reads a variable the engine cannot see for the request (see
 *    `ROUTING_RULE_VARIABLES`). A deployment with no running instances is
 *    never eligible.
 * 2. The request may use the shards of every eligible deployment and takes
 *    any free capacity among them; load routing ranks their shards by score.
 *    With `loadRouting.affinity.priority` above 0 it prefers the eligible
 *    deployments at the best (lowest) priority while they have free slots,
 *    then spills to the others. When all of them are full it queues for them
 *    (or is rejected); it never uses an ineligible deployment.
 * 3. When no deployment is eligible, the request goes to the endpoint's
 *    default deployment regardless of its rule.
 * 4. An endpoint whose routing was never saved has no default and routes
 *    across all its deployments by free capacity. Engines reload routing at
 *    most every 5 minutes, so a save takes effect within 5 minutes.
 *
 * Priority (stored as `endpoint_deployments.rank`, `routingRank` in the API):
 * an integer per deployment, lower wins, null = none. It orders deployments
 * for `loadRouting.affinity.priority`: every eligible deployment at the best
 * priority gets that bonus. Equal priorities are allowed and share load; ties
 * order by deployment id. The default is the deployment with the lowest
 * priority (then lowest deployment id). Full-list writes assign
 * `ROUTING_PRIORITY_STEP` gaps (10, 20, 30, ...) so one deployment can later
 * move between two others without renumbering.
 *
 * Every write names the routing version it read (`routingVersion` on the
 * endpoint); a write against a version that has since changed is refused.
 *
 * An endpoint's shed rule (stored as `endpoints.shed_rule`, `shedRule` in the
 * API) uses the same format. The engine evaluates it before slot selection
 * and rejects a request it accepts with `429 capacity_exceeded`. It may also
 * read `target.free_slot_pct`, there the free slots in percent across every
 * deployment the request may use (its eligible deployments). A rule that
 * cannot be evaluated never sheds. Writes take CEL text; `ENDPOINT_RULE_OFF` or
 * null clears it.
 *
 * An endpoint's queue rule (`endpoints.queue_rule`, `queueRule` in the API)
 * uses the same format and write values. When the endpoint's router queue is
 * on, only a request the rule accepts may wait in the queue when no slot is
 * free; any other request gets `429 capacity_exceeded` at once. Null lets
 * every request queue. A rule that cannot be evaluated never queues. It may
 * not read `target.free_slot_pct`.
 *
 * The API stores and accepts the JSON AST (`RoutingRuleSchema`). The UI shows
 * and accepts CEL text: `routingRuleToCEL` prints an AST and `parseRoutingRuleCEL`
 * parses the CEL subset the AST can represent.
 */

export const ROUTING_RULE_NUMERIC_VARS = [
  "request.input_token_count",
  "request.max_output_tokens",
  "request.num_images",
  "request.image_tokens",
  "request.uncached_estimate",
  "request.session_bucket",
  "target.free_slot_pct",
] as const;

export const ROUTING_RULE_BOOLEAN_VARS = [
  "request.us_geo_required",
  "request.stream",
  "request.has_tools",
  "request.is_openrouter",
] as const;

export const ROUTING_RULE_STRING_VARS = [
  "request.prefix_family",
  "request.tools_family",
] as const;

type RoutingRuleVariable =
  | (typeof ROUTING_RULE_NUMERIC_VARS)[number]
  | (typeof ROUTING_RULE_BOOLEAN_VARS)[number]
  | (typeof ROUTING_RULE_STRING_VARS)[number];

/**
 * What each variable means and when the engine cannot see it. Large requests
 * reach the engine as a presigned URL rather than an inline body, so every
 * body-derived variable is unknown for them.
 */
export const ROUTING_RULE_VARIABLES: Record<RoutingRuleVariable, string> = {
  "request.input_token_count":
    "prompt tokens, counted with the endpoint's tokenizer; unknown for presigned requests",
  "request.max_output_tokens":
    "max_completion_tokens, else max_tokens, else max_output_tokens; 0 when the request sets none (the engine sizes the output to the context left); unknown for presigned requests",
  "request.num_images":
    "image parts across the request's messages or input; unknown for presigned requests",
  "request.image_tokens":
    "prompt tokens the request's images become on the model's image processor (Kimi-K3, GLM-5.3-Flash and DeepSeek-V4.1 checkpoints), exact from each base64 image's PNG/JPEG/WebP/GIF header; an image whose size can't be read (an http(s) URL) counts as the processor's largest image; 0 without images; unknown for presigned requests, or when the request has images and the endpoint's tokenizer repo is not one of those models. Add it to request.input_token_count, which never counts images",
  "request.uncached_estimate":
    "prompt tokens no instance is known to have cached: the prompt times the share of it the sticky probe's deepest prefix match does not cover (a match at the probe's max depth of 256 messages covers up to the last assistant message); on a miss and on a session-only match whose history was never seen, the prompt past its tools and leading system messages when those were served within the last 2 hours, else the whole prompt; on a session-only match whose history was seen, the share past the seen depth, also when another instance saw it (an upper bound: the session's instance may hold more); unknown for presigned requests, requests without sticky routing, and endpoints with a single instance",
  "request.session_bucket":
    "0-99, a stable hash of the request's conversation, so request.session_bucket < 40 takes a fixed 40% slice of conversations and every turn of one stays in it: hashed from the sticky session id when the request carries one, else from its tools and messages through the first message after the leading system messages (the sticky prefix key every later turn shares); needs no sticky routing; unknown for presigned and Responses-API requests and ones with only system messages",
  "request.us_geo_required":
    "the request must be served by US workers (the US-only API hostnames); always known",
  "request.stream": "the request streams; unknown for presigned requests",
  "request.has_tools":
    "the request lists at least one tool; unknown for presigned requests",
  "request.is_openrouter": "the request came from OpenRouter; always known",
  "request.prefix_family":
    'client family: 12 uppercase hex characters hashed from the request\'s tools and first 4 messages, so requests that share those share it; "" with fewer than 4 messages or for presigned and Responses-API requests; always known. Compare with == "<id>" or in ["<id>", ...]',
  "request.tools_family":
    'tool-set family: 12 uppercase hex characters hashed from the request\'s tool definitions alone, so requests that declare the same tools share it whatever their messages; "" when the request has no tools or is presigned; always known. Compare with == "<id>" or in ["<id>", ...]',
  "target.free_slot_pct":
    "shed rules only: the free slots in percent (0-100) across every deployment the request may use (its eligible deployments); unknown in routing rules and queue rules, so a rule that reads it is false",
};

/** The `shedRule` and `queueRule` write value that clears the rule, like null. */
export const ENDPOINT_RULE_OFF = "none";

/** Whether a rule reads a `target.*` variable, which only shed rules can see. */
export function routingRuleReadsTarget(rule: RoutingRule): boolean {
  return routingRuleToCEL(rule).includes("target.");
}

const NumericVar = z.enum(ROUTING_RULE_NUMERIC_VARS);
const BooleanVar = z.enum(ROUTING_RULE_BOOLEAN_VARS);
const StringVar = z.enum(ROUTING_RULE_STRING_VARS);

type NumericVarName = (typeof ROUTING_RULE_NUMERIC_VARS)[number];
type BooleanVarName = (typeof ROUTING_RULE_BOOLEAN_VARS)[number];
type StringVarName = (typeof ROUTING_RULE_STRING_VARS)[number];

/** Most values one `str_in` node may list. */
export const ROUTING_RULE_MAX_STRING_VALUES = 32;
const FAMILY_ID_PATTERN = /^([0-9A-F]{12})?$/;
const FAMILY_ID_FORMAT =
  'A family id is 12 uppercase hex characters (e.g. "B58F1D182419"), or "" for none';

const ARITH_OPS = ["add", "sub", "mul", "div", "mod"] as const;
const CMP_OPS = ["eq", "neq", "lt", "lte", "gt", "gte"] as const;

type ArithOp = (typeof ARITH_OPS)[number];
type CmpOp = (typeof CMP_OPS)[number];

export type RoutingNumExpr =
  | { op: "num"; value: number }
  | { op: "var"; name: NumericVarName }
  | { op: ArithOp; left: RoutingNumExpr; right: RoutingNumExpr };

export type RoutingBoolExpr =
  | { op: "bool"; value: boolean }
  | { op: "bool_var"; name: BooleanVarName }
  | { op: "str_in"; name: StringVarName; values: string[] }
  | { op: CmpOp; left: RoutingNumExpr; right: RoutingNumExpr }
  | { op: "and" | "or"; args: RoutingBoolExpr[] }
  | { op: "not"; arg: RoutingBoolExpr };

const NumExprSchema: z.ZodType<RoutingNumExpr> = z.lazy(() =>
  z.union([
    z.object({ op: z.literal("num"), value: z.number().finite() }).strict(),
    z.object({ op: z.literal("var"), name: NumericVar }).strict(),
    z
      .object({
        op: z.enum(ARITH_OPS),
        left: NumExprSchema,
        right: NumExprSchema,
      })
      .strict(),
  ]),
);

const BoolExprSchema: z.ZodType<RoutingBoolExpr> = z.lazy(() =>
  z.union([
    z.object({ op: z.literal("bool"), value: z.boolean() }).strict(),
    z.object({ op: z.literal("bool_var"), name: BooleanVar }).strict(),
    z
      .object({
        op: z.literal("str_in"),
        name: StringVar,
        values: z
          .array(z.string().regex(FAMILY_ID_PATTERN, FAMILY_ID_FORMAT))
          .min(1)
          .max(
            ROUTING_RULE_MAX_STRING_VALUES,
            `At most ${ROUTING_RULE_MAX_STRING_VALUES} values in one list`,
          ),
      })
      .strict(),
    z
      .object({
        op: z.enum(CMP_OPS),
        left: NumExprSchema,
        right: NumExprSchema,
      })
      .strict(),
    z
      .object({
        op: z.enum(["and", "or"]),
        args: z.array(BoolExprSchema).min(1),
      })
      .strict(),
    z.object({ op: z.literal("not"), arg: BoolExprSchema }).strict(),
  ]),
);

/** The gap between priorities a full-list routing write assigns. */
export const ROUTING_PRIORITY_STEP = 10;
/** Highest priority a caller may set; keeps arithmetic far from int4 limits. */
export const ROUTING_PRIORITY_MAX = 1_000_000;

// Guard against pathological rules (deeply nested or huge ASTs).
export const ROUTING_RULE_MAX_NODES = 200;
export const ROUTING_RULE_MAX_DEPTH = 20;

function measure(node: unknown, depth = 1): { nodes: number; depth: number } {
  if (node === null || typeof node !== "object") return { nodes: 0, depth: 0 };
  const children = Object.values(node).flatMap((value) =>
    Array.isArray(value) ? value : [value],
  );
  let nodes = 1;
  let maxDepth = depth;
  for (const child of children) {
    if (child && typeof child === "object") {
      const measured = measure(child, depth + 1);
      nodes += measured.nodes;
      maxDepth = Math.max(maxDepth, measured.depth);
    }
  }
  return { nodes, depth: maxDepth };
}

/** A deployment's routing rule, stored on `endpoint_deployments.rule`. */
export const RoutingRuleSchema = z
  .object({
    version: z.literal(1),
    expr: BoolExprSchema,
  })
  .superRefine((rule, ctx) => {
    const { nodes, depth } = measure(rule.expr);
    if (nodes > ROUTING_RULE_MAX_NODES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Rule has ${nodes} nodes (max ${ROUTING_RULE_MAX_NODES})`,
      });
    }
    if (depth > ROUTING_RULE_MAX_DEPTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Rule is nested ${depth} deep (max ${ROUTING_RULE_MAX_DEPTH})`,
      });
    }
  });

export type RoutingRule = z.infer<typeof RoutingRuleSchema>;

/**
 * `RoutingRuleSchema` for API inputs and outputs. The OpenAPI generator can't
 * emit a recursive schema inline, so the spec shows an opaque object while
 * every value is still validated against the full AST schema.
 */
export const RoutingRuleApiSchema = z
  .custom<RoutingRule>()
  .superRefine((value, ctx) => {
    const parsed = RoutingRuleSchema.safeParse(value);
    if (parsed.success) return;
    for (const issue of parsed.error.issues) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: issue.path,
        message: issue.message,
      });
    }
  })
  .describe(
    `Routing rule JSON AST: \`{ "version": 1, "expr": BoolExpr }\`. Number variables: ${ROUTING_RULE_NUMERIC_VARS.join(", ")}. Boolean variables: ${ROUTING_RULE_BOOLEAN_VARS.join(", ")}. String variables (str_in): ${ROUTING_RULE_STRING_VARS.join(", ")}.`,
  );

/**
 * How to write routing for API, CLI and MCP callers (agents): semantics, the
 * rule JSON shape, variables, operators and an example. Shown as the
 * `updateEndpointRouting` operation description.
 */
export const ROUTING_RULE_API_HELP = [
  "How routing works: a deployment is eligible for a request when its rule evaluates to true; a null rule is always eligible. A rule is false when it divides or takes a modulo by zero, or reads a variable the engine cannot see for that request. A deployment with no running instances is never eligible. The request may use every eligible deployment and takes any free capacity among them; with the endpoint's loadRouting.affinity.priority above 0 it prefers the eligible deployments at the best (lowest) priority while they have free slots, then spills to the others. When all of them are full it queues for them, and it never uses an ineligible deployment. When none is eligible, the default serves it regardless of its rule. A save takes effect within 5 minutes.",
  `Priority: each deployment has an integer priority (\`routingRank\` on deployment reads), lower wins; equal priorities are allowed and share load (ties order by deployment id); the default is the deployment with the lowest priority (then lowest id). This operation replaces the whole order: \`deployments\` in priority order, each \`{ deploymentId, rule }\`, get priorities ${ROUTING_PRIORITY_STEP}, ${ROUTING_PRIORITY_STEP * 2}, ${ROUTING_PRIORITY_STEP * 3}, ... and the first becomes the default. Deployments you leave out keep their rules and follow the listed ones in their current order. To change one deployment's rule or priority without touching the others, use set-deployment-routing instead.`,
  "Concurrency: pass `expectedRoutingVersion`, the endpoint's `routingVersion` from get-endpoint (or from the conflict error). If the routing changed since you read it, the write is refused with CONFLICT and the current routing, and nothing is saved: re-read, re-apply your change, retry. Optional `reason` and `clientTag` (script name, agent session) are recorded on the endpoint.routing_changed event.",
  'Rule shape (JSON, not CEL): `{ "version": 1, "expr": BoolExpr }`.',
  `BoolExpr: \`{ "op": "bool", "value": true|false }\`, \`{ "op": "bool_var", "name": <boolean variable> }\`, \`{ "op": "str_in", "name": <string variable>, "values": ["<id>", ...] }\` (true when the variable equals one of 1 to ${ROUTING_RULE_MAX_STRING_VALUES} values; each value is 12 uppercase hex characters or ""), \`{ "op": "eq"|"neq"|"lt"|"lte"|"gt"|"gte", "left": NumExpr, "right": NumExpr }\`, \`{ "op": "and"|"or", "args": [BoolExpr, ...] }\` (at least one), \`{ "op": "not", "arg": BoolExpr }\`.`,
  'NumExpr: `{ "op": "num", "value": <finite number> }`, `{ "op": "var", "name": <number variable> }`, `{ "op": "add"|"sub"|"mul"|"div"|"mod", "left": NumExpr, "right": NumExpr }`.',
  `Number variables: ${ROUTING_RULE_NUMERIC_VARS.join(", ")}. Boolean variables: ${ROUTING_RULE_BOOLEAN_VARS.join(", ")}. String variables: ${ROUTING_RULE_STRING_VARS.join(", ")}. No other variables exist.`,
  `Variables: ${Object.entries(ROUTING_RULE_VARIABLES)
    .map(([name, meaning]) => `${name}: ${meaning}`)
    .join("; ")}.`,
  `Limits: at most ${ROUTING_RULE_MAX_NODES} nodes and ${ROUTING_RULE_MAX_DEPTH} levels of nesting.`,
  'Example: keep prompts over 256k tokens off dep_small, and let everything use dep_large: `{ "endpointId": "<id>", "expectedRoutingVersion": "<routingVersion>", "deployments": [ { "deploymentId": "dep_small", "rule": { "version": 1, "expr": { "op": "lte", "left": { "op": "var", "name": "request.input_token_count" }, "right": { "op": "num", "value": 262144 } } } }, { "deploymentId": "dep_large", "rule": null } ] }`. Prompts up to 256k can use free capacity on either deployment; larger prompts only use dep_large. To keep small prompts off dep_large, give it the opposite rule (`request.input_token_count > 262144`).',
  'Example: isolate one client family on dep_family and keep it off dep_main: dep_family gets `{ "version": 1, "expr": { "op": "str_in", "name": "request.prefix_family", "values": ["B58F1D182419"] } }` and dep_main gets `{ "version": 1, "expr": { "op": "not", "arg": { "op": "str_in", "name": "request.prefix_family", "values": ["B58F1D182419"] } } }`. Requests with no family ("") match the negated rule and stay on dep_main.',
  'Example: send one client, identified by its tool set, to dep_client: dep_client gets `{ "version": 1, "expr": { "op": "str_in", "name": "request.tools_family", "values": ["1DE7BDB78E18"] } }`. Unlike request.prefix_family, the tools family stays the same across the client\'s conversations.',
].join("\n\n");

// ---------------------------------------------------------------------------
// Printer: AST -> CEL
// ---------------------------------------------------------------------------

const ARITH_SYMBOL = { add: "+", sub: "-", mul: "*", div: "/", mod: "%" };
const CMP_SYMBOL = {
  eq: "==",
  neq: "!=",
  lt: "<",
  lte: "<=",
  gt: ">",
  gte: ">=",
};
const ARITH_PRECEDENCE = { add: 1, sub: 1, mul: 2, div: 2, mod: 2 };

function unknownOp(expr: { op: string }): string {
  return `<unsupported op "${String(expr.op)}" - upgrade inf>`;
}

function numToCEL(
  expr: RoutingNumExpr,
  parentPrec = 0,
  isRight = false,
): string {
  if (expr.op === "num") return String(expr.value);
  if (expr.op === "var") return expr.name;
  // An op newer than this build (server/client version skew): print, don't crash.
  if (!(expr.op in ARITH_PRECEDENCE)) return unknownOp(expr);
  const prec = ARITH_PRECEDENCE[expr.op];
  const text = `${numToCEL(expr.left, prec)} ${ARITH_SYMBOL[expr.op]} ${numToCEL(expr.right, prec, true)}`;
  const needsParens = prec < parentPrec || (prec === parentPrec && isRight);
  return needsParens ? `(${text})` : text;
}

function boolToCEL(
  expr: RoutingBoolExpr,
  parent: "and" | "or" | "not" | null,
): string {
  switch (expr.op) {
    case "bool":
      return String(expr.value);
    case "bool_var":
      return expr.name;
    case "str_in": {
      const [only] = expr.values;
      const text =
        expr.values.length === 1 && only !== undefined
          ? `${expr.name} == ${JSON.stringify(only)}`
          : `${expr.name} in [${expr.values.map((value) => JSON.stringify(value)).join(", ")}]`;
      return parent === "not" ? `(${text})` : text;
    }
    case "not":
      return `!${boolToCEL(expr.arg, "not")}`;
    case "and":
    case "or": {
      const [only] = expr.args;
      if (expr.args.length === 1 && only) return boolToCEL(only, parent);
      const joiner = expr.op === "and" ? " && " : " || ";
      const text = expr.args.map((arg) => boolToCEL(arg, expr.op)).join(joiner);
      return parent && parent !== expr.op ? `(${text})` : text;
    }
    default: {
      if (!(expr.op in CMP_SYMBOL)) return unknownOp(expr);
      const text = `${numToCEL(expr.left)} ${CMP_SYMBOL[expr.op]} ${numToCEL(expr.right)}`;
      return parent === "not" ? `(${text})` : text;
    }
  }
}

/** The CEL text for a rule's expression, as shown in the UI. */
export function routingRuleToCEL(rule: RoutingRule): string {
  return boolToCEL(rule.expr, null);
}

// ---------------------------------------------------------------------------
// Parser: CEL -> AST
// ---------------------------------------------------------------------------

export type RoutingRuleParseResult =
  | { ok: true; rule: RoutingRule }
  | { ok: false; message: string; position: number };

type Token = { text: string; position: number };

class CELParseError extends Error {
  constructor(
    message: string,
    readonly position: number,
  ) {
    super(message);
  }
}

// Sticky: matches one token exactly at `lastIndex`.
const TOKEN_PATTERN =
  /\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|[A-Za-z_]\w*(?:\.[A-Za-z_]\w*)*|"[^"\\\n]*"|&&|\|\||==|!=|<=|>=|[!<>+\-*/%()[\],]/y;

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let position = 0;
  for (;;) {
    const skipped = source.slice(position).search(/\S/);
    if (skipped === -1) return tokens;
    position += skipped;
    TOKEN_PATTERN.lastIndex = position;
    const match = TOKEN_PATTERN.exec(source);
    if (!match) {
      throw new CELParseError(
        source[position] === '"'
          ? "Unterminated string; strings are double-quoted and can't contain \\ or line breaks"
          : `Unexpected character "${source[position]}"`,
        position,
      );
    }
    tokens.push({ text: match[0], position });
    position += match[0].length;
  }
}

type Typed =
  | { kind: "num"; expr: RoutingNumExpr }
  | { kind: "bool"; expr: RoutingBoolExpr }
  | { kind: "str_var"; name: StringVarName }
  | { kind: "str"; value: string };

const KIND_LABEL: Record<Typed["kind"], string> = {
  num: "a number",
  bool: "a true/false condition",
  str_var: "a string variable",
  str: "a string",
};

const CMP_BY_SYMBOL: Record<string, CmpOp> = {
  "==": "eq",
  "!=": "neq",
  "<": "lt",
  "<=": "lte",
  ">": "gt",
  ">=": "gte",
};
const ADD_BY_SYMBOL: Record<string, ArithOp> = { "+": "add", "-": "sub" };
const MUL_BY_SYMBOL: Record<string, ArithOp> = {
  "*": "mul",
  "/": "div",
  "%": "mod",
};

const ALLOWED_VARS = [
  ...ROUTING_RULE_NUMERIC_VARS,
  ...ROUTING_RULE_BOOLEAN_VARS,
  ...ROUTING_RULE_STRING_VARS,
].join(", ");

/**
 * Recursive-descent parser for the CEL subset the rule AST represents:
 * `||` < `&&` < comparison (non-associative) < `+ -` < `* / %` < unary `!`.
 * Unary minus applies to number literals only. A string variable is only
 * compared with `== "x"` or `in ["x", ...]`.
 */
class CELParser {
  private index = 0;

  constructor(
    private readonly tokens: Token[],
    private readonly sourceLength: number,
  ) {}

  parseRule(): RoutingBoolExpr {
    if (this.tokens.length === 0) {
      throw new CELParseError("Rule is empty", 0);
    }
    const start = this.peek();
    const expr = this.bool(this.or(), start);
    const trailing = this.peek();
    if (trailing) {
      throw new CELParseError(
        `Unexpected "${trailing.text}"`,
        trailing.position,
      );
    }
    return expr;
  }

  private peek(): Token | undefined {
    return this.tokens[this.index];
  }

  private next(): Token {
    const token = this.tokens[this.index];
    if (!token) {
      throw new CELParseError("Unexpected end of rule", this.sourceLength);
    }
    this.index += 1;
    return token;
  }

  private bool(typed: Typed, at: Token | undefined): RoutingBoolExpr {
    if (typed.kind === "bool") return typed.expr;
    throw new CELParseError(
      `Expected a true/false condition, got ${KIND_LABEL[typed.kind]}`,
      at?.position ?? 0,
    );
  }

  private num(typed: Typed, at: Token | undefined): RoutingNumExpr {
    if (typed.kind === "num") return typed.expr;
    throw new CELParseError(
      `Expected a number, got ${KIND_LABEL[typed.kind]}`,
      at?.position ?? 0,
    );
  }

  private or(): Typed {
    return this.logical("||", "or", () => this.and());
  }

  private and(): Typed {
    return this.logical("&&", "and", () => this.comparison());
  }

  private logical(
    symbol: string,
    op: "and" | "or",
    operand: () => Typed,
  ): Typed {
    const firstToken = this.peek();
    const first = operand();
    if (this.peek()?.text !== symbol) return first;
    const args = [this.bool(first, firstToken)];
    while (this.peek()?.text === symbol) {
      this.next();
      const token = this.peek();
      args.push(this.bool(operand(), token));
    }
    return { kind: "bool", expr: { op, args } };
  }

  private comparison(): Typed {
    const leftToken = this.peek();
    const left = this.additive();
    if (left.kind === "str_var") return this.stringComparison(left.name);
    const op = CMP_BY_SYMBOL[this.peek()?.text ?? ""];
    if (!op) return left;
    this.next();
    const rightToken = this.peek();
    const right = this.additive();
    const chained = this.peek();
    if (chained && CMP_BY_SYMBOL[chained.text]) {
      throw new CELParseError(
        "Comparisons can't be chained; join them with &&",
        chained.position,
      );
    }
    return {
      kind: "bool",
      expr: {
        op,
        left: this.num(left, leftToken),
        right: this.num(right, rightToken),
      },
    };
  }

  private stringComparison(name: StringVarName): Typed {
    const opToken = this.peek();
    if (opToken?.text === "==") {
      this.next();
      return {
        kind: "bool",
        expr: { op: "str_in", name, values: [this.stringLiteral(name)] },
      };
    }
    if (opToken?.text === "in") {
      this.next();
      const open = this.peek();
      if (open?.text !== "[") {
        throw new CELParseError(
          'Expected "[" after in',
          open?.position ?? this.sourceLength,
        );
      }
      this.next();
      const values = [this.stringLiteral(name)];
      while (this.peek()?.text === ",") {
        this.next();
        values.push(this.stringLiteral(name));
      }
      const close = this.peek();
      if (close?.text !== "]") {
        throw new CELParseError(
          'Expected "," or "]"',
          close?.position ?? this.sourceLength,
        );
      }
      this.next();
      return { kind: "bool", expr: { op: "str_in", name, values } };
    }
    throw new CELParseError(
      `${name} is a string: compare it with == "<id>" or in ["<id>", ...]`,
      opToken?.position ?? this.sourceLength,
    );
  }

  private stringLiteral(name: StringVarName): string {
    const token = this.next();
    if (!token.text.startsWith('"')) {
      throw new CELParseError(
        `${name} compares to a double-quoted string, e.g. "B58F1D182419"`,
        token.position,
      );
    }
    const value = token.text.slice(1, -1);
    if (!FAMILY_ID_PATTERN.test(value)) {
      throw new CELParseError(FAMILY_ID_FORMAT, token.position);
    }
    return value;
  }

  private additive(): Typed {
    return this.arithmetic(ADD_BY_SYMBOL, () => this.multiplicative());
  }

  private multiplicative(): Typed {
    return this.arithmetic(MUL_BY_SYMBOL, () => this.unary());
  }

  private arithmetic(
    opsBySymbol: Record<string, ArithOp>,
    operand: () => Typed,
  ): Typed {
    const leftToken = this.peek();
    let left = operand();
    let op = opsBySymbol[this.peek()?.text ?? ""];
    while (op) {
      this.next();
      const rightToken = this.peek();
      const right = operand();
      left = {
        kind: "num",
        expr: {
          op,
          left: this.num(left, leftToken),
          right: this.num(right, rightToken),
        },
      };
      op = opsBySymbol[this.peek()?.text ?? ""];
    }
    return left;
  }

  private unary(): Typed {
    const token = this.peek();
    if (token?.text === "!") {
      this.next();
      const argToken = this.peek();
      return {
        kind: "bool",
        expr: { op: "not", arg: this.bool(this.unary(), argToken) },
      };
    }
    if (token?.text === "-") {
      this.next();
      const literal = this.next();
      const value = Number(literal.text);
      if (!/^\d/.test(literal.text) || !Number.isFinite(value)) {
        throw new CELParseError(
          "Minus only applies to number literals; write 0 - x instead",
          literal.position,
        );
      }
      return { kind: "num", expr: { op: "num", value: -value } };
    }
    return this.primary();
  }

  private primary(): Typed {
    const token = this.next();
    if (token.text === "(") {
      const inner = this.or();
      const close = this.peek();
      if (close?.text !== ")") {
        throw new CELParseError(
          'Expected ")"',
          close?.position ?? this.sourceLength,
        );
      }
      this.next();
      return inner;
    }
    if (/^\d/.test(token.text)) {
      const value = Number(token.text);
      if (!Number.isFinite(value)) {
        throw new CELParseError("Number is too large", token.position);
      }
      return { kind: "num", expr: { op: "num", value } };
    }
    if (token.text === "true" || token.text === "false") {
      return {
        kind: "bool",
        expr: { op: "bool", value: token.text === "true" },
      };
    }
    const numeric = NumericVar.safeParse(token.text);
    if (numeric.success) {
      return { kind: "num", expr: { op: "var", name: numeric.data } };
    }
    const boolean = BooleanVar.safeParse(token.text);
    if (boolean.success) {
      return { kind: "bool", expr: { op: "bool_var", name: boolean.data } };
    }
    const string = StringVar.safeParse(token.text);
    if (string.success) {
      return { kind: "str_var", name: string.data };
    }
    if (token.text.startsWith('"')) {
      return { kind: "str", value: token.text.slice(1, -1) };
    }
    if (/^[A-Za-z_]/.test(token.text)) {
      throw new CELParseError(
        `Unknown variable "${token.text}". Allowed: ${ALLOWED_VARS}`,
        token.position,
      );
    }
    throw new CELParseError(`Unexpected "${token.text}"`, token.position);
  }
}

/**
 * Parse CEL text into a validated routing rule. `position` on failure is the
 * character offset of the offending token.
 */
export function parseRoutingRuleCEL(source: string): RoutingRuleParseResult {
  let expr: RoutingBoolExpr;
  try {
    expr = new CELParser(tokenize(source), source.length).parseRule();
  } catch (error) {
    if (error instanceof CELParseError) {
      return { ok: false, message: error.message, position: error.position };
    }
    throw error;
  }
  const parsed = RoutingRuleSchema.safeParse({ version: 1, expr });
  if (!parsed.success) {
    return {
      ok: false,
      message: parsed.error.issues[0]?.message ?? "Invalid rule",
      position: 0,
    };
  }
  return { ok: true, rule: parsed.data };
}
